import { createApi } from '@reduxjs/toolkit/query/react';
import { Response } from '../../models/shared';
import {
  CheckoutOrderDTO, CheckoutSessionDTO, CreateCheckoutSessionDTO, CreatePaymentAttemptDTO, UpdateCheckoutSessionDTO,
} from '../../models/checkout-session';
import { PaymentDTO } from '../../models/payment';
import { SharedTicketDTO } from '../../models/shared-ticket';
import baseQueryWithReauth from './base';

interface EventArgs {
  businessId: string;
  eventId: string;
}

// Checkout nuevo: sesión de compra en el servidor, orden sin pago al terminar
// "Tus datos" y cada intento de pago aparte sobre la misma orden. Los errores
// de negocio (sin stock, celular inválido, pago rechazado) los muestra cada
// pantalla en su lugar, por eso los endpoints son silenciosos.
const checkoutSessionApi = createApi({
  reducerPath: 'checkoutSessionApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['checkout-session'],
  endpoints: (build) => ({
    createCheckoutSession: build.mutation<CheckoutSessionDTO, EventArgs & { body: CreateCheckoutSessionDTO }>({
      query: ({ businessId, eventId, body }) => ({
        url: `businesses/${businessId}/events/${eventId}/checkout-sessions`,
        method: 'POST',
        body,
      }),
      extraOptions: { silent: true },
      transformResponse: (response: Response<CheckoutSessionDTO>) => response.data as CheckoutSessionDTO,
    }),
    getCheckoutSession: build.query<CheckoutSessionDTO, EventArgs & { token: string }>({
      query: ({ businessId, eventId, token }) => ({
        url: `businesses/${businessId}/events/${eventId}/checkout-sessions/${token}`,
      }),
      extraOptions: { silent: true },
      transformResponse: (response: Response<CheckoutSessionDTO>) => response.data as CheckoutSessionDTO,
      providesTags: (result, error, { token }) => [{ type: 'checkout-session', id: token }],
    }),
    updateCheckoutSession: build.mutation<CheckoutSessionDTO, EventArgs & { token: string, body: UpdateCheckoutSessionDTO }>({
      query: ({
        businessId, eventId, token, body,
      }) => ({
        url: `businesses/${businessId}/events/${eventId}/checkout-sessions/${token}`,
        method: 'PATCH',
        body,
      }),
      extraOptions: { silent: true },
      transformResponse: (response: Response<CheckoutSessionDTO>) => response.data as CheckoutSessionDTO,
      invalidatesTags: (result, error, { token }) => [{ type: 'checkout-session', id: token }],
    }),
    renewCheckoutSession: build.mutation<CheckoutSessionDTO, EventArgs & { token: string }>({
      query: ({ businessId, eventId, token }) => ({
        url: `businesses/${businessId}/events/${eventId}/checkout-sessions/${token}/renew`,
        method: 'POST',
      }),
      extraOptions: { silent: true },
      transformResponse: (response: Response<CheckoutSessionDTO>) => response.data as CheckoutSessionDTO,
      invalidatesTags: (result, error, { token }) => [{ type: 'checkout-session', id: token }],
    }),
    // Suelta la reserva de una compra que se deja (cambió las entradas o
    // "Empezar de nuevo"): sin esto retenía stock hasta que venciera.
    cancelCheckoutSession: build.mutation<CheckoutSessionDTO, EventArgs & { token: string }>({
      query: ({ businessId, eventId, token }) => ({
        url: `businesses/${businessId}/events/${eventId}/checkout-sessions/${token}/cancel`,
        method: 'POST',
      }),
      extraOptions: { silent: true },
      transformResponse: (response: Response<CheckoutSessionDTO>) => response.data as CheckoutSessionDTO,
      invalidatesTags: (result, error, { token }) => [{ type: 'checkout-session', id: token }],
    }),
    createCheckoutOrder: build.mutation<CheckoutOrderDTO, EventArgs & { token: string }>({
      query: ({ businessId, eventId, token }) => ({
        url: `businesses/${businessId}/events/${eventId}/checkout-sessions/${token}/order`,
        method: 'POST',
      }),
      extraOptions: { silent: true },
      transformResponse: (response: Response<CheckoutOrderDTO>) => response.data as CheckoutOrderDTO,
      invalidatesTags: (result, error, { token }) => [{ type: 'checkout-session', id: token }],
    }),
    createPaymentAttempt: build.mutation<PaymentDTO, EventArgs & { customId: string, body: CreatePaymentAttemptDTO }>({
      query: ({
        businessId, eventId, customId, body,
      }) => ({
        url: `businesses/${businessId}/events/${eventId}/orders/custom-id/${customId}/payments`,
        method: 'POST',
        body,
      }),
      // La sesión trae el pago abierto de la orden: sin releerla, volver a Pago
      // después de pedir el CVU no sabía que había una transferencia en curso.
      invalidatesTags: ['checkout-session'],
      extraOptions: { silent: true },
      transformResponse: (response: Response<PaymentDTO>) => response.data as PaymentDTO,
    }),
    // Una sola entrada compartida (/entrada/:token): solo su QR, sin la orden.
    getSharedTicket: build.query<SharedTicketDTO | null, { token: string }>({
      query: ({ token }) => ({ url: `items/public/${token}` }),
      extraOptions: { silent: true },
      transformResponse: (response: Response<SharedTicketDTO>) => response.data ?? null,
    }),
  }),
});

export const {
  useCreateCheckoutSessionMutation,
  useGetCheckoutSessionQuery,
  useUpdateCheckoutSessionMutation,
  useRenewCheckoutSessionMutation,
  useCreateCheckoutOrderMutation,
  useCancelCheckoutSessionMutation,
  useCreatePaymentAttemptMutation,
  useGetSharedTicketQuery,
} = checkoutSessionApi;
export { checkoutSessionApi };
